import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const icons = ['Heart', 'Sparkles', 'Utensils', 'PartyPopper', 'Ring'];

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    icon: 'Heart',
    features: [],
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title: service.title,
        description: service.description,
        image_url: service.image_url,
        icon: service.icon,
        features: service.features || [],
      });
    } else {
      setEditingService(null);
      setFormData({ title: '', description: '', image_url: '', icon: 'Heart', features: [] });
    }
    setFeatureInput('');
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSaving(true);
    const token = getAuthToken();

    try {
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Service updated!');
      } else {
        await axios.post(`${API}/admin/services`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Service created!');
      }
      setIsDialogOpen(false);
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;

    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Service deleted!');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Services</h1>
        <Button onClick={() => openDialog()} data-testid="add-service-btn" className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">
          <Plus size={18} className="mr-2" /> Add Service
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <div key={service.id} data-testid={`service-item-${index}`} className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <div className="h-48 overflow-hidden">
              <img src={service.image_url} alt={service.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-2">{service.title}</h3>
              <p className="text-[#6B7280] text-sm line-clamp-2 mb-4">{service.description}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openDialog(service)} className="border-[#E8DFD0]">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(service.id)} className="text-red-500 border-red-200 hover:bg-red-50">
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Title *</Label>
              <Input name="title" value={formData.title} onChange={handleInputChange} className="input-elegant" placeholder="Service title" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Description *</Label>
              <Textarea name="description" value={formData.description} onChange={handleInputChange} className="input-elegant min-h-[100px]" placeholder="Service description" />
            </div>
            <div>
              <FileUpload
                currentUrl={formData.image_url}
                onUpload={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                accept="image/*"
                label="Upload Service Image"
              />
              <div className="mt-2">
                <Label className="text-sm font-medium mb-2 block">Or paste Image URL</Label>
                <Input name="image_url" value={formData.image_url} onChange={handleInputChange} className="input-elegant" placeholder="https://..." />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Icon</Label>
              <Select value={formData.icon} onValueChange={(value) => setFormData((prev) => ({ ...prev, icon: value }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {icons.map((icon) => <SelectItem key={icon} value={icon}>{icon}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Features</Label>
              <div className="flex gap-2 mb-2">
                <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} className="input-elegant flex-1" placeholder="Add feature" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())} />
                <Button type="button" onClick={addFeature} variant="outline">Add</Button>
              </div>
              <div className="space-y-2">
                {formData.features.map((feature, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#F5F5F0] p-2 rounded">
                    <span className="text-sm">{feature}</span>
                    <button onClick={() => removeFeature(i)} className="text-red-500 text-sm">Remove</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <Button onClick={() => setIsDialogOpen(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white">{isSaving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
